import React, { useEffect, useState } from "react";
import { Terminal, Sun, Moon, Menu, X } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { useTheme } from "../context/ThemeContext";
import PrivacyConfirmModal from "./PrivacyConfirmModal";

interface NavbarProps {
  isMenuOpen: boolean;
  setIsMenuOpen: (value: boolean) => void;
}

interface NavLink {
  id: string;
  labelEn: string;
  labelFr: string;
}

const Navbar = ({ isMenuOpen, setIsMenuOpen }: NavbarProps) => {
  const { language, setLanguage } = useLanguage();
  const { theme, setTheme } = useTheme();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const isDark = theme === "dark";

  const navLinks: NavLink[] = [
    { id: "services", labelEn: "Expertise", labelFr: "Expertise" },
    { id: "solutions", labelEn: "Solutions", labelFr: "Solutions" },
    { id: "examples", labelEn: "Examples", labelFr: "Exemples" },
    { id: "process", labelEn: "Process", labelFr: "Processus" },
    { id: "faq", labelEn: "FAQ", labelFr: "FAQ" },
    { id: "contact", labelEn: "Contact Us", labelFr: "Contactez-nous" },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId: string) => {
    const targetSection = document.getElementById(sectionId);
    if (targetSection) {
      const navbarHeight = 64; // height of the navbar (h-16 = 64px)
      const sectionTop = targetSection.getBoundingClientRect().top + window.scrollY - navbarHeight;
      window.scrollTo({
        top: sectionTop,
        behavior: 'smooth'
      });
      setIsMenuOpen(false);
    }
  };

  const handleCTAClick = () => {
    setIsModalOpen(true);
  };

  const handleConfirm = () => {
    const formUrl = language === "en" 
      ? 'https://tally.so/r/wvJ2jl'
      : 'https://tally.so/r/nPb4oQ';
    const win = window.open(formUrl, 'DynovantContactForm', 'noopener,noreferrer');
    if (win) win.opener = null;
    setIsModalOpen(false);
  };

  return (
    <>
      <nav className={`
        fixed w-full z-50 transition-all duration-300 pt-2
        ${isScrolled 
          ? isDark 
            ? "bg-black/80 backdrop-blur-sm" 
            : "bg-white/80 backdrop-blur-sm"
          : "bg-transparent"
        }
      `}>
        <div className="max-w-8x1 mx-auto px-4 sm:px-6 lg:px-12">
          <div className="flex items-center justify-between h-16">
            {/* Logo and brand */}
            <div 
              className="flex items-center space-x-2 cursor-pointer" 
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            >
              <span className={`text-sm sm:text-base md:text-lg lg:text-xl xl:text-2xl font-bold whitespace-nowrap ${
                isDark ? "text-white" : "text-black"
              }`}>
                Dynovant Systems
              </span>
              <Terminal
                className={`h-4 w-4 sm:h-5 sm:w-5 md:h-6 md:w-6 lg:h-7 lg:w-7 xl:h-8 xl:w-8 ${
                  isDark ? "text-green-500" : "text-green-600"
                }`}
              />
            </div>

            {/* Navigation Links for larger screens */}
            <div className="hidden md:flex items-center justify-center flex-1 px-4">
              {navLinks.map((link) => (
                <button
                  key={link.id}
                  onClick={() => scrollToSection(link.id)}
                  className={`
                    px-3 py-2 rounded-lg text-base lg:text-lg
                    transition-all duration-300
                    font-bold
                    ${isDark 
                      ? "text-gray-300 hover:text-green-400 hover:bg-green-500/10" 
                      : "text-gray-700 hover:text-green-800 hover:bg-green-900/10"
                    }
                  `}
                >
                  {language === "en" ? link.labelEn : link.labelFr}
                </button>
              ))}
            </div>

            {/* Right Section: Get Started, Language, Theme */}
            <div className="flex items-center gap-4">
              {/* Get Started Button - Always visible */}
              <button
                onClick={handleCTAClick}
                className={`
                  px-4 py-2 rounded-lg text-base lg:text-lg font-semibold whitespace-nowrap
                  transition-all duration-300
                  ${isDark
                    ? "bg-green-500 text-black hover:bg-green-400"
                    : "bg-green-600 text-white hover:bg-green-500"
                  }
                `}
              >
                {language === "en" ? "Get Started" : "Démarrer"}
              </button>

              {/* Language Switcher */}
              <div className="hidden md:flex space-x-2">
                <button
                  onClick={() => setLanguage("en")}
                  className={`
                    w-8 h-8 rounded-full overflow-hidden
                    transition-all duration-300
                    ${language === "en" 
                      ? isDark 
                        ? "ring-2 ring-green-500" 
                        : "ring-2 ring-green-600"
                      : "opacity-50 hover:opacity-100"
                    }
                  `}
                >
                  <img
                    src="/assets/USA_flag.svg"
                    alt="English"
                    className="w-full h-full object-cover"
                  />
                </button>
                <button
                  onClick={() => setLanguage("fr")}
                  className={`
                    w-8 h-8 rounded-full overflow-hidden
                    transition-all duration-300
                    ${language === "fr" 
                      ? isDark 
                        ? "ring-2 ring-green-500" 
                        : "ring-2 ring-green-600"
                      : "opacity-50 hover:opacity-100"
                    }
                  `}
                >
                  <img
                    src="/assets/france-flag-icon.svg"
                    alt="Français"
                    className="w-full h-full object-cover"
                  />
                </button>
              </div>

              {/* Light/Dark Mode Toggle */}
              <button
                onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
                className={`
                  hidden md:flex
                  w-9 h-9
                  items-center 
                  justify-center 
                  rounded-full
                  transition-all
                  duration-300
                  ml-2
                  ${isDark 
                    ? "text-white hover:text-green-400" 
                    : "text-gray-700 hover:text-green-800"
                  }
                `}
              >
                {isDark ? <Sun className="w-7 h-7" /> : <Moon className="w-7 h-7" />}
              </button>

              {/* Mobile Menu Button */}
              <div className="md:hidden">
                <button
                  onClick={() => setIsMenuOpen(!isMenuOpen)}
                  className={`p-2 rounded-lg transition-colors ${
                    isDark ? "text-white hover:bg-gray-800" : "text-black hover:bg-gray-200"
                  }`}
                >
                  {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                </button>
              </div>
            </div>
          </div>

          {/* Mobile Menu */}
          <div className={`
            md:hidden 
            fixed inset-x-0 top-16 
            transition-all duration-300 ease-in-out
            ${isMenuOpen ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-full pointer-events-none"}
            ${isDark ? "bg-black/95" : "bg-white/95"}
            backdrop-blur-sm
            border-b
            ${isDark ? "border-green-500/20" : "border-green-900/20"}
          `}>
            <div className="px-4 py-6 space-y-4">
              {navLinks.map((link) => (
                <button
                  key={link.id}
                  onClick={() => scrollToSection(link.id)}
                  className={`
                    w-full px-4 py-3 rounded-lg text-left text-lg
                    transition-all duration-300
                    font-bold
                    ${isDark 
                      ? "text-gray-300 hover:text-green-400 hover:bg-green-500/10" 
                      : "text-gray-700 hover:text-green-800 hover:bg-green-900/10"
                    }
                  `}
                >
                  {language === "en" ? link.labelEn : link.labelFr}
                </button>
              ))}

              <div className="flex items-center justify-between px-4">
                {/* Language Switcher */}
                <div className="flex space-x-2">
                  <button
                    onClick={() => setLanguage("en")}
                    className={`
                      w-10 h-10 rounded-full overflow-hidden
                      transition-all duration-300
                      ${language === "en" 
                        ? isDark 
                          ? "ring-2 ring-green-500" 
                          : "ring-2 ring-green-600"
                        : "opacity-50 hover:opacity-100"
                      }
                    `}
                  >
                    <img
                      src="/assets/USA_flag.svg"
                      alt="English"
                      className="w-full h-full object-cover"
                    />
                  </button>
                  <button
                    onClick={() => setLanguage("fr")}
                    className={`
                      w-10 h-10 rounded-full overflow-hidden
                      transition-all duration-300
                      ${language === "fr" 
                        ? isDark 
                          ? "ring-2 ring-green-500" 
                          : "ring-2 ring-green-600"
                        : "opacity-50 hover:opacity-100"
                      }
                    `}
                  >
                    <img
                      src="/assets/france-flag-icon.svg"
                      alt="Français"
                      className="w-full h-full object-cover"
                    />
                  </button>
                </div>

                {/* Light/Dark Mode Toggle */}
                <button
                  onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
                  className={`
                    w-10 h-10
                    flex 
                    items-center 
                    justify-center 
                    rounded-full
                    transition-all
                    duration-300
                    ${isDark 
                      ? "text-white hover:text-green-400" 
                      : "text-gray-700 hover:text-green-800"
                    }
                  `}
                >
                  {isDark ? <Sun className="w-6 h-6" /> : <Moon className="w-6 h-6" />}
                </button>
              </div>
            </div>
          </div>
        </div>
      </nav>

      <PrivacyConfirmModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirm={handleConfirm}
      />
    </>
  );
};

export default Navbar;
